import { type ICharacter, type PageRowViewModel } from "@next-platform/client-common-core";
import React from "react";

import DiscoverRowCards from "@/pages/Discover/components/DiscoverRowCards";
import DiscoverRowError from "@/pages/Discover/components/DiscoverRowError";
import DiscoverRowHeader from "@/pages/Discover/components/DiscoverRowHeader";
import DiscoverRowSkeletons from "@/pages/Discover/components/DiscoverRowSkeletons";
import DiscoverRowTrack from "@/pages/Discover/components/DiscoverRowTrack";
import useDiscoverRowLayout from "@/pages/Discover/hooks/useDiscoverRowLayout";
import useDiscoverRowScroll from "@/pages/Discover/hooks/useDiscoverRowScroll";
import useDiscoverRowTitle from "@/pages/Discover/hooks/useDiscoverRowTitle";
import readDiscoverRowContentKey from "@/pages/Discover/readDiscoverRowContentKey";

type DiscoverContentRowProps = {
  row?: PageRowViewModel<ICharacter>;
  onSelectCharacter: (character: ICharacter) => void;
  onLessLikeThis: (character: ICharacter) => void;
};

type DiscoverRowContentProps = {
  row?: PageRowViewModel<ICharacter>;
  skeletonCount: number;
  onSelectCharacter: (character: ICharacter) => void;
  onLessLikeThis: (character: ICharacter) => void;
};

type DiscoverRowHeaderProps = {
  title: string;
};

const MIN_ITEMS_FOR_TWO_ROWS = 4;

const DiscoverRowHeaderContent = ({ title }: DiscoverRowHeaderProps) => {
  if (title.length === 0) {
    return null;
  }

  return <DiscoverRowHeader title={title} />;
};

const DiscoverRowContent = ({ row, skeletonCount, onSelectCharacter, onLessLikeThis }: DiscoverRowContentProps) => {
  if (row?.status === "success") {
    return <DiscoverRowCards characters={row.items} onSelectCharacter={onSelectCharacter} onLessLikeThis={onLessLikeThis} />;
  }

  return <DiscoverRowSkeletons count={skeletonCount} />;
};

const DiscoverRowErrorContent = ({ row, title }: { row: Extract<PageRowViewModel<ICharacter>, { status: "error" }>; title: string }) => {
  return (
    <>
      <DiscoverRowHeaderContent title={title} />
      <DiscoverRowError onRetry={row.retry} />
    </>
  );
};

const DiscoverRowTrackContent = ({
  row,
  title,
  skeletonCount,
  refTrack,
  trackStyle,
  shouldUseTwoRows,
  canScrollPrevious,
  canScrollNext,
  onScrollPrevious,
  onScrollNext,
  previousLabel,
  nextLabel,
  onSelectCharacter,
  onLessLikeThis,
}: {
  row?: PageRowViewModel<ICharacter>;
  title: string;
  skeletonCount: number;
  refTrack: React.Ref<HTMLDivElement | null>;
  trackStyle: React.CSSProperties;
  shouldUseTwoRows: boolean;
  canScrollPrevious: boolean;
  canScrollNext: boolean;
  onScrollPrevious: () => void;
  onScrollNext: () => void;
  previousLabel: string;
  nextLabel: string;
  onSelectCharacter: (character: ICharacter) => void;
  onLessLikeThis: (character: ICharacter) => void;
}) => {
  return (
    <>
      <DiscoverRowHeaderContent title={title} />

      <DiscoverRowTrack
        refTrack={refTrack}
        trackStyle={trackStyle}
        shouldUseTwoRows={shouldUseTwoRows}
        canScrollPrevious={canScrollPrevious}
        canScrollNext={canScrollNext}
        onScrollPrevious={onScrollPrevious}
        onScrollNext={onScrollNext}
        previousLabel={previousLabel}
        nextLabel={nextLabel}
      >
        <DiscoverRowContent row={row} skeletonCount={skeletonCount} onSelectCharacter={onSelectCharacter} onLessLikeThis={onLessLikeThis} />
      </DiscoverRowTrack>
    </>
  );
};

const DiscoverContentRow = ({ row, onSelectCharacter, onLessLikeThis }: DiscoverContentRowProps) => {
  const { refTrack, trackElement, trackStyle, isMobile, skeletonCount } = useDiscoverRowLayout();

  const isTrackVisible = row?.status !== "error" && row?.status !== "empty";
  const contentKey = readDiscoverRowContentKey({ row, skeletonCount });

  const { canScrollPrevious, canScrollNext, handleScrollPrevious, handleScrollNext, previousLabel, nextLabel } = useDiscoverRowScroll({
    trackElement: isTrackVisible ? trackElement : null,
    contentKey,
  });

  const titleKey = row?.block.parameters.titleKey ?? "";
  const title = useDiscoverRowTitle(titleKey);

  const shouldUseTwoRows = isMobile && (row?.status !== "success" || row.items.length >= MIN_ITEMS_FOR_TWO_ROWS);

  if (row?.status === "empty") {
    return null;
  }

  return (
    <section data-testid="DiscoverContentRow" className="flex min-w-0 flex-col gap-4">
      {row?.status === "error" ? (
        <DiscoverRowErrorContent row={row} title={title} />
      ) : (
        <DiscoverRowTrackContent
          row={row}
          title={title}
          skeletonCount={skeletonCount}
          refTrack={refTrack}
          trackStyle={trackStyle}
          shouldUseTwoRows={shouldUseTwoRows}
          canScrollPrevious={canScrollPrevious}
          canScrollNext={canScrollNext}
          onScrollPrevious={handleScrollPrevious}
          onScrollNext={handleScrollNext}
          previousLabel={previousLabel}
          nextLabel={nextLabel}
          onSelectCharacter={onSelectCharacter}
          onLessLikeThis={onLessLikeThis}
        />
      )}
    </section>
  );
};

export default React.memo(DiscoverContentRow);
